import { ApiClient } from "../api-client";
import { ApiClientError } from "../errors";
import schemaValidators from "./schema-validators";

export default class BasicAuthClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	login = async ({ email, password }: { email: string; password: string }) => {
		try {
			const formData = new URLSearchParams();
			formData.append("email", email);
			formData.append("password", password);

			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/login`, {
				credentials: "include",
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: formData.toString(),
			});

			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.login.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	createAccount = async ({ email, password }: { email: string; password: string }) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth`, {
				credentials: "include",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
					password,
				}),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.createAccount.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	sendVerificationEmail = async ({ email }: { email: string }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/basic-auth/email-verification`,
				{
					credentials: "include",
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						email,
					}),
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	verifyEmail = async ({
		accountId,
		emailVerificationToken,
	}: {
		accountId: string;
		emailVerificationToken: string;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/verify-email`, {
				credentials: "include",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					accountId,
					emailVerificationToken,
				}),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.verifyEmail.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	sendResetPasswordEmail = async ({ email }: { email: string }) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/reset-password`, {
				credentials: "include",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
				}),
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	resetPassword = async ({
		accountId,
		passwordRestToken,
		password,
	}: {
		accountId: string;
		passwordRestToken: string;
		password: string;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/password`, {
				credentials: "include",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					accountId,
					passwordRestToken,
					password,
				}),
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
